import { useHandler } from "@redotech/react-util/hook";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { ClientUploader } from "@redotech/redo-merchant-app-common/client/upload";
import { SettingsPermissions } from "@redotech/redo-model/permissions";
import {
  AI_VERIFICATION_TYPES,
  type AiVerificationType,
  InputContentType,
  InputPartType,
  InputValidation,
} from "@redotech/redo-model/return-flow";
import { InputState } from "@redotech/redo-return-ui/flows/return-flow/input";
import { StepTypeDetailsProps } from "@redotech/redo-return-ui/flows/step";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { ImageUpload } from "@redotech/redo-web/image-upload";
import { Text } from "@redotech/redo-web/text";
import { memo, useId, useMemo } from "react";

const typeOptions: InputPartType[] = [
  InputPartType.IMAGES,
  InputPartType.TEXTAREA,
  InputPartType.VIDEOS,
  InputPartType.DATE,
];

function typeLabel(type: InputPartType) {
  switch (type) {
    case InputPartType.IMAGES:
      return "Images";
    case InputPartType.VIDEOS:
      return "Videos";
    case InputPartType.DATE:
      return "Date";
    default:
      return "Text";
  }
}

const contentTypeOptions: InputContentType[] = [
  InputContentType.ANY,
  InputContentType.NUMBERS,
  InputContentType.LETTERS,
];

function contentTypeLabel(type: InputContentType): string {
  switch (type) {
    case InputContentType.ANY:
      return "Any";
    case InputContentType.NUMBERS:
      return "Numbers only";
    case InputContentType.LETTERS:
      return "Letters only";
  }
}

export const InputDetails = memo(function InputDetails({
  stepSelect,
  state,
  setState,
}: StepTypeDetailsProps<InputState>) {
  const canEditReturnsSettings = useHasPermission(
    SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
  );
  const handlePrompt = useHandler((prompt: string) => {
    setState((state) => ({ ...state, prompt }));
  });
  const handleDescription = useHandler((description: string) => {
    setState((state) => ({ ...state, description }));
  });
  const handleType = useHandler((selectedType: InputPartType) => {
    setState((state) => ({ ...state, type: selectedType }));
  });
  const handleRequired = useHandler((value: boolean) => {
    setState((state) => ({
      ...state,
      validation: value ? InputValidation.REQUIRED : InputValidation.OPTIONAL,
    }));
  });
  const handleExampleImage = useHandler((exampleImage: URL | undefined) => {
    setState((state) => ({ ...state, exampleImage }));
  });
  const handleAiVerification = useHandler((value: boolean) => {
    const defaultType = AI_VERIFICATION_TYPES[0];
    setState((state) => ({
      ...state,
      aiVerification: value ? defaultType : undefined,
      aiVerificationStrictness: value ? 5 : undefined,
    }));
  });
  const handleAiVerificationType = useHandler((type: AiVerificationType) => {
    setState((state) => ({ ...state, aiVerification: type }));
  });
  const handleAiVerificationStrictness = useHandler((value: number) => {
    setState((state) => ({ ...state, aiVerificationStrictness: value }));
  });
  const handleMinLength = useHandler((value: string) => {
    const parsed = value === "" ? undefined : parseInt(value, 10);
    setState((state) => ({
      ...state,
      minLength: parsed !== undefined && !isNaN(parsed) ? parsed : undefined,
    }));
  });
  const handleMaxLength = useHandler((value: string) => {
    const parsed = value === "" ? undefined : parseInt(value, 10);
    setState((state) => ({
      ...state,
      maxLength: parsed !== undefined && !isNaN(parsed) ? parsed : undefined,
    }));
  });
  const handleContentType = useHandler((contentType: InputContentType) => {
    setState((state) => ({ ...state, contentType }));
  });
  const handleCustomRegex = useHandler((customRegex: string) => {
    setState((state) => ({ ...state, customRegex: customRegex || undefined }));
  });
  const handleCustomRegexErrorMessage = useHandler((msg: string) => {
    setState((state) => ({
      ...state,
      customRegexErrorMessage: msg || undefined,
    }));
  });
  const handleAdvancedValidation = useHandler((value: boolean) => {
    setState((state) => ({ ...state, useAdvancedValidation: value }));
  });
  const exampleImageId = useId();

  const regexError = useMemo(() => {
    if (!state.customRegex) return undefined;
    try {
      new RegExp(state.customRegex);
      return undefined;
    } catch {
      return "Invalid regex pattern";
    }
  }, [state.customRegex]);

  const strictnessOptions = useMemo(
    () =>
      Array.from({ length: 10 }, (_, i) => i + 1).map((n) => ({
        type: "text" as const,
        id: String(n),
        text: `${n}${n === 1 ? " (least strict)" : n === 10 ? " (most strict)" : ""}`,
        value: n,
      })),
    [],
  );

  const selectedStrictnessItem = useMemo(() => {
    const val = state.aiVerificationStrictness ?? 5;
    return strictnessOptions.find((o) => o.value === val);
  }, [state.aiVerificationStrictness, strictnessOptions]);

  const verificationTypeOptions = useMemo(
    () =>
      AI_VERIFICATION_TYPES.map((key) => ({
        type: "text" as const,
        id: key,
        text: key
          .split("_")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" "),
        value: key,
      })),
    [],
  );

  const selectedVerificationType = useMemo(() => {
    if (!state.aiVerification) return undefined;
    return verificationTypeOptions.find(
      (o) => o.value === state.aiVerification,
    );
  }, [state.aiVerification, verificationTypeOptions]);

  const typeListItems = useMemo<
    Array<{
      type: "text";
      id: InputPartType;
      text: string;
      value: InputPartType;
    }>
  >(
    () =>
      typeOptions.map((type) => ({
        type: "text" as const,
        id: type,
        text: typeLabel(type),
        value: type,
      })),
    [],
  );

  const selectedTypeItem = useMemo<
    | { type: "text"; id: InputPartType; text: string; value: InputPartType }
    | undefined
  >(
    () =>
      state.type
        ? {
            type: "text" as const,
            id: state.type,
            text: typeLabel(state.type),
            value: state.type,
          }
        : undefined,
    [state.type],
  );

  const contentTypeListItems = useMemo(
    () =>
      contentTypeOptions.map((ct) => ({
        type: "text" as const,
        id: ct,
        text: contentTypeLabel(ct),
        value: ct,
      })),
    [],
  );

  const selectedContentTypeItem = useMemo(() => {
    const ct = state.contentType ?? InputContentType.ANY;
    return {
      type: "text" as const,
      id: ct,
      text: contentTypeLabel(ct),
      value: ct,
    };
  }, [state.contentType]);

  return (
    <ClientUploader>
      <RedoToggle
        disabled={!canEditReturnsSettings}
        label="Required"
        setValue={handleRequired}
        value={state.validation === InputValidation.REQUIRED}
      />
      <Flex dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          Prompt
        </Text>
        <RedoTextareaInputField
          errorText={state.attempted && !state.prompt ? "Required" : undefined}
          placeholder="Enter prompt..."
          readOnly={!canEditReturnsSettings}
          setValue={handlePrompt}
          value={state.prompt}
        />
      </Flex>
      <Flex dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          Description
        </Text>
        <RedoTextareaInputField
          placeholder="Enter description..."
          readOnly={!canEditReturnsSettings}
          setValue={handleDescription}
          value={state.description}
        />
      </Flex>
      <RedoSingleSelectDropdownInput
        disabled={!canEditReturnsSettings}
        flexGrow={false}
        label="Answer type"
        options={typeListItems}
        optionSelected={({ item }) => handleType(item.value)}
        selectedItem={selectedTypeItem}
        size="xs"
      />
      {state.type === InputPartType.IMAGES && (
        <>
          <Flex dir="column" gap="xs">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              Image
            </Text>
            <ImageUpload
              disabled={!canEditReturnsSettings}
              id={exampleImageId}
              onChange={handleExampleImage}
              value={state.exampleImage}
            />
            <Text fontSize="xs" fontWeight="regular" textColor="tertiary">
              Optional. Upload an image to show the customer an example of what
              you&apos;re looking for.
            </Text>
          </Flex>
          <Divider />
          <RedoToggle
            disabled={!canEditReturnsSettings}
            label="AI verification"
            setValue={handleAiVerification}
            value={!!state.aiVerification}
          />
          {state.aiVerification && (
            <>
              <RedoSingleSelectDropdownInput
                disabled={!canEditReturnsSettings}
                flexGrow={false}
                label="Verification type"
                options={verificationTypeOptions}
                optionSelected={({ item }) =>
                  handleAiVerificationType(item.value)
                }
                selectedItem={selectedVerificationType}
                size="xs"
              />
              <RedoSingleSelectDropdownInput
                disabled={!canEditReturnsSettings}
                flexGrow={false}
                label="Strictness"
                options={strictnessOptions}
                optionSelected={({ item }) =>
                  handleAiVerificationStrictness(item.value)
                }
                selectedItem={selectedStrictnessItem}
                size="xs"
              />
            </>
          )}
        </>
      )}
      {state.type === InputPartType.TEXTAREA && (
        <>
          <Divider />
          <Text fontSize="xs" fontWeight="semibold" textColor="secondary">
            Text validation
          </Text>
          <RedoToggle
            disabled={!canEditReturnsSettings}
            label="Advanced validation"
            setValue={handleAdvancedValidation}
            value={!!state.useAdvancedValidation}
          />
          {!state.useAdvancedValidation && (
            <>
              <Flex dir="row" gap="md">
                <Flex dir="column" gap="xs" grow={1}>
                  <RedoTextInput
                    inputTypeHint="number"
                    label="Min characters"
                    min={0}
                    placeholder="No minimum"
                    setValue={handleMinLength}
                    size="sm"
                    value={state.minLength?.toString() ?? ""}
                  />
                </Flex>
                <Flex dir="column" gap="xs" grow={1}>
                  <RedoTextInput
                    inputTypeHint="number"
                    label="Max characters"
                    min={1}
                    placeholder="No maximum"
                    setValue={handleMaxLength}
                    size="sm"
                    value={state.maxLength?.toString() ?? ""}
                  />
                </Flex>
              </Flex>
              <RedoSingleSelectDropdownInput
                disabled={!canEditReturnsSettings}
                flexGrow={false}
                label="Content type"
                options={contentTypeListItems}
                optionSelected={({ item }) => handleContentType(item.value)}
                selectedItem={selectedContentTypeItem}
                size="xs"
              />
            </>
          )}
          {state.useAdvancedValidation && (
            <>
              <RedoTextInput
                errorText={regexError}
                label="Custom regex pattern"
                placeholder="e.g. ^[A-Z]{2}\d{4}$"
                setValue={handleCustomRegex}
                size="sm"
                value={state.customRegex ?? ""}
              />
              {state.customRegex && (
                <RedoTextInput
                  label="Validation error message"
                  placeholder="Input does not match required format"
                  setValue={handleCustomRegexErrorMessage}
                  size="sm"
                  value={state.customRegexErrorMessage ?? ""}
                />
              )}
            </>
          )}
        </>
      )}
      <Divider />
      <Flex dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          Next
        </Text>
        {stepSelect({
          value: state.next,
          valueChange: (next) => setState({ ...state, next }),
        })}
      </Flex>
    </ClientUploader>
  );
});
